// src/components/EditProfile/EditProfile.jsx
// Editor de perfil UNIFICADO (un solo lugar para gestionar tu identidad):
//   · Tu cuenta  → nombre, país, teléfono (recolección básica / KYC progresivo)
//   · Tu tienda  → titular, sobre mí, banner, link vanity, horario de atención
// Siempre editás TU PROPIO perfil (PATCH /api/me edita al usuario autenticado). Al abrir hace
// GET /api/me para prellenar con datos autoritativos (el login trae un select mínimo, así que
// authUser no siempre tiene storefront/teléfono). Reusado por el perfil privado y el público.
import React, { useState, useEffect } from 'react';
import { FiEdit2, FiX, FiAlertTriangle, FiUser, FiHome } from 'react-icons/fi';
import { apiFetch } from '../../services/apiClient';
import { useAuth } from '../../Context/AuthContext';
import './EditProfile.css';

// Presets de banner (id guardado en bannerColor) — espejo del render en TraderProfile
const BANNER_PRESETS = [
  { id: 'verde',   css: 'linear-gradient(135deg,#00d412,#007850)' },
  { id: 'cian',    css: 'linear-gradient(135deg,#22d3ee,#2775ca)' },
  { id: 'magenta', css: 'linear-gradient(135deg,#d946ef,#7b3fe4)' },
  { id: 'oro',     css: 'linear-gradient(135deg,#f0b90b,#f4511e)' },
  { id: 'noche',   css: 'linear-gradient(135deg,#243b27,#0a0e0b)' },
];
const DOW = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
const COUNTRIES = [
  { code: 'CR', label: 'Costa Rica' }, { code: 'CO', label: 'Colombia' },
  { code: 'VE', label: 'Venezuela' }, { code: 'MX', label: 'México' },
  { code: 'AR', label: 'Argentina' }, { code: 'PE', label: 'Perú' },
  { code: 'CL', label: 'Chile' },     { code: 'BR', label: 'Brasil' },
  { code: 'EC', label: 'Ecuador' },   { code: 'BO', label: 'Bolivia' },
  { code: 'PY', label: 'Paraguay' },  { code: 'UY', label: 'Uruguay' },
  { code: 'PA', label: 'Panamá' },    { code: 'US', label: 'Estados Unidos' },
  { code: 'ES', label: 'España' },    { code: 'other', label: 'Otro' },
];

// Loader: trae el perfil completo antes de montar el formulario (así los initializers de
// useState reciben datos reales). Cae a `initial` si el fetch falla (offline).
export default function EditProfile({ initial, onClose, onSaved }) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let alive = true;
    apiFetch('/api/me')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive) setSrc({ ...(initial || {}), ...(d || {}) }); })
      .catch(() => { if (alive) setSrc(initial || {}); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="ep-overlay" onClick={onClose}>
      <div className="ep-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ep-head">
          <h3><FiEdit2 className="ep-head-ic" /> Editar mi perfil</h3>
          <button className="ep-close" onClick={onClose} aria-label="Cerrar"><FiX /></button>
        </div>
        {src == null
          ? <div className="ep-loading">Cargando tu perfil…</div>
          : <ProfileForm src={src} onClose={onClose} onSaved={onSaved} />}
      </div>
    </div>
  );
}

function ProfileForm({ src, onClose, onSaved }) {
  const { updateUser } = useAuth();
  const [name, setName]         = useState(src.name || '');
  const [country, setCountry]   = useState(src.country && src.country !== 'all' ? src.country : '');
  const [phone, setPhone]       = useState(src.phone || '');
  const [headline, setHeadline] = useState(src.headline || '');
  const [bio, setBio]           = useState(src.bio || '');
  const [banner, setBanner]     = useState(src.bannerColor || 'verde');
  const [slug, setSlug]         = useState(src.slug || '');
  const [openFrom, setOpenFrom] = useState(src.openFrom ?? '');
  const [openTo, setOpenTo]     = useState(src.openTo ?? '');
  const [days, setDays]         = useState(() => (src.openDays ? String(src.openDays).split(',').map(Number) : [1, 2, 3, 4, 5]));
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);

  const linkSlug = (slug || '').trim().toLowerCase();
  const toggleDay = (d) => setDays((ds) => ds.includes(d) ? ds.filter((x) => x !== d) : [...ds, d]);

  const save = async () => {
    if (!name.trim()) { setError('El nombre no puede quedar vacío.'); return; }
    setSaving(true); setError(null);
    try {
      const res = await apiFetch('/api/me', {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          name: name.trim(),
          country: country || 'all',
          phone: phone.trim() || null,
          headline, bio, bannerColor: banner, slug,
          openFrom: openFrom === '' ? null : Number(openFrom),
          openTo:   openTo   === '' ? null : Number(openTo),
          openDays: days.length ? days.join(',') : null,
        }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.message || 'No se pudo guardar'); }
      const updated = await res.json();
      updateUser(updated);          // refresca el usuario en sesión (nombre/país/etc. app-wide)
      onSaved && onSaved(updated);
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <div className="ep-body">
      {/* ── TU CUENTA ── */}
      <div className="ep-section-head"><FiUser /> Tu cuenta</div>

      <div className="ep-field">
        <label>Nombre público</label>
        <input maxLength={40} value={name} onChange={(e) => setName(e.target.value)} placeholder="Cómo te ven los demás" />
      </div>

      <div className="ep-grid2">
        <div className="ep-field">
          <label>País</label>
          <select value={country} onChange={(e) => setCountry(e.target.value)}>
            <option value="">— Elegir —</option>
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
        </div>
        <div className="ep-field">
          <label>Teléfono <span className="ep-count">privado</span></label>
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+506 8888 8888" />
        </div>
      </div>
      <span className="ep-hint">Tu teléfono es privado (recolección básica para verificación). No aparece en tu perfil público.</span>

      {/* ── TU TIENDA ── */}
      <div className="ep-section-head ep-section-head-2"><FiHome /> Tu tienda</div>

      <div className="ep-field">
        <label>Banner</label>
        <div className="ep-banner-presets">
          {BANNER_PRESETS.map((p) => (
            <button key={p.id} type="button" className={`ep-banner-preset ${banner === p.id ? 'active' : ''}`}
              style={{ background: p.css }} onClick={() => setBanner(p.id)} title={p.id} aria-label={p.id} />
          ))}
        </div>
      </div>

      <div className="ep-field">
        <label>Titular <span className="ep-count">{headline.length}/60</span></label>
        <input maxLength={60} value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="Ej: Cambio USDT rápido y seguro 🇨🇷" />
      </div>

      <div className="ep-field">
        <label>Sobre mí <span className="ep-count">{bio.length}/280</span></label>
        <textarea maxLength={280} rows={3} value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Contá tu experiencia, horarios, métodos preferidos…" />
      </div>

      <div className="ep-field">
        <label>Tu link personalizado</label>
        <div className="ep-slug-input"><span>bitplify.io/@</span><input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="tu-nombre" /></div>
        <span className="ep-hint">Letras, números y guiones. {linkSlug ? `→ bitplify.io/@${linkSlug}` : '(opcional)'}</span>
      </div>

      <div className="ep-field">
        <label>Horario de atención</label>
        <div className="ep-hours-row">
          <select value={openFrom} onChange={(e) => setOpenFrom(e.target.value)}>
            <option value="">—</option>
            {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{h}:00</option>)}
          </select>
          <span className="ep-hours-sep">a</span>
          <select value={openTo} onChange={(e) => setOpenTo(e.target.value)}>
            <option value="">—</option>
            {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{h}:00</option>)}
          </select>
        </div>
        <div className="ep-days-row">
          {DOW.map((d, i) => (
            <button key={i} type="button" className={`ep-day ${days.includes(i) ? 'active' : ''}`} onClick={() => toggleDay(i)}>{d}</button>
          ))}
        </div>
        <span className="ep-hint">Mostrá "🟢 Abierto ahora" cuando atendés.</span>
      </div>

      <p className="ep-guard">🔒 No incluyas datos de contacto externos: las operaciones van por la plataforma con vendedores verificados y disputas.</p>
      {error && <div className="ep-error"><FiAlertTriangle /> {error}</div>}

      <div className="ep-actions">
        <button className="ep-cancel" onClick={onClose}>Cancelar</button>
        <button className="ep-save" onClick={save} disabled={saving}>{saving ? 'Guardando…' : 'Guardar cambios'}</button>
      </div>
    </div>
  );
}
